import React, { useContext, useEffect, useState } from "react";
import { deleteTemplate, fetchTemplates } from "../services/TemplateAPI";
import { Template } from "../types/Template";
import TemplateList from "../components/templates/TemplateList";
import CreateTemplateForm from "../components/templates/CreateTemplateForm";
import EditTemplateForm from "../components/templates/EditTemplateForm";
import FormComponent from "../components/generic/FormComponent";
import { MESSAGE_TYPES, Messages, MessageType } from "../constants/Messages";
import AlertBanner from "../components/generic/AlertBanner";
import { UserContext } from "../context/UserContext";
import ConfirmationModal from "../components/generic/ConfirmationModal"; // Import the ConfirmationModal component

const TemplatePage: React.FC = () => {
    const [ templates, setTemplates ] = useState<Template[]>([]);
    const [ isCreateModalOpen, setIsCreateModalOpen ] = useState(false);
    const [ isEditModalOpen, setIsEditModalOpen ] = useState(false);
    const [ selectedTemplateId, setSelectedTemplateId ] = useState<number | null>(null);
    const [ alert, setAlert ] = useState<{ message: string; type: MessageType } | null>(null);
    const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState(false); // State for delete confirmation modal
    const [ templateToDelete, setTemplateToDelete ] = useState<number | null>(null); // State for the template to be deleted
    const [ duplicateTemplate, setDuplicateTemplate ] = useState<Template | null>(null); // State for the duplicated template
    const { isLoggedIn } = useContext(UserContext);

    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const data = await fetchTemplates();
                setTemplates(data);
            } catch (error: any) {
                console.error("Error fetching templates:", error.message);
            }
        };

        loadTemplates();
    }, [ isLoggedIn, isCreateModalOpen, isEditModalOpen ]);

    const handleViewEdit = (id: number | undefined, currentLabel: string) => {
        if (id !== undefined) {
            setSelectedTemplateId(id);
            setIsEditModalOpen(true);
        }
    };

    const handleTemplateUpdated = (updatedTemplate: Template) => {
        try {
            setTemplates((prevTemplates) =>
                prevTemplates.map((template) =>
                    template.id === updatedTemplate.id ? updatedTemplate : template,
                ),
            );
            setAlert({
                message: Messages.SUCCESS_UPDATE_TEMPLATE,
                type: MESSAGE_TYPES.SUCCESS,
            });
        } catch (error: any) {
            setAlert({
                message: error.message || Messages.ERROR_UPDATE_TEMPLATE,
                type: MESSAGE_TYPES.ERROR,
            });
        }
    };

    const handleDeleteTemplate = async (id: number) => {
        try {
            await deleteTemplate(id);
            setTemplates((prev) => prev.filter((template) => template.id !== id));
            setAlert({
                message: Messages.SUCCESS_DELETE_TEMPLATE,
                type: MESSAGE_TYPES.SUCCESS,
            });
        } catch (error: any) {
            setAlert({
                message: error.message || Messages.ERROR_DELETE_TEMPLATE,
                type: MESSAGE_TYPES.ERROR,
            });
        }
    };

    const confirmDeleteTemplate = (id: number) => {
        setTemplateToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (templateToDelete !== null) {
            handleDeleteTemplate(templateToDelete);
        }
        setIsDeleteModalOpen(false);
        setTemplateToDelete(null);
    };

    const handleDuplicateTemplate = (id: number | undefined, name: string) => {
        const template = templates.find((t) => t.id === id);
        if (template) {
            setDuplicateTemplate(template);
            setIsCreateModalOpen(true);
        }
    };

    return (
        <div>
            <FormComponent title="Template Management">
                <div>
                    <button className={"primary"} onClick={() => setIsCreateModalOpen(true)}>
                        Create Template
                    </button>
                    {alert && (
                        <div>
                            <AlertBanner message={alert.message} message_type={alert.type}
                                         onClose={() => setAlert(null)}/>
                        </div>
                    )}
                    <TemplateList
                        items={templates.map((template) => ({
                            id: template.id,
                            label: template.name, // Assuming 'name' is the property to be used as 'label'
                        }))}
                        onDelete={confirmDeleteTemplate}
                        onEdit={(id, label) => handleViewEdit(id, label)} // Pass both id and label
                        onDuplicate={(id, label) => handleDuplicateTemplate(id, label)} // Pass both id and label for duplication
                    />
                </div>
            </FormComponent>

            {isCreateModalOpen && (
                <CreateTemplateForm
                    onClose={() => {
                        setIsCreateModalOpen(false);
                        setDuplicateTemplate(null); // Reset duplicate template state
                    }}
                    initialValues={duplicateTemplate || undefined} // Pass duplicate template details if available
                />
            )}

            {isEditModalOpen && selectedTemplateId && (
                <EditTemplateForm
                    templateId={selectedTemplateId}
                    onClose={() => setIsEditModalOpen(false)}
                    onTemplateUpdated={handleTemplateUpdated}
                />
            )}

            {isDeleteModalOpen && (
                <ConfirmationModal
                    title="Confirm Delete"
                    message="Are you sure you want to delete this template?"
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setIsDeleteModalOpen(false)}
                />
            )}
        </div>
    );
};

export default TemplatePage;